/**
 * RPC clients for the CLI.
 *
 * Deliberately scoped to the **two CLI-facing groups only** — `HandoffRpc` and
 * `SessionRpc`. We import those group modules directly (not the `@repo/contracts`
 * barrel), so the bundled CLI never contains the rest of the API surface. Each
 * client is a standard per-group `Context.Service` (mirroring the worker/VTM
 * pattern), built over an HTTP transport whose base URL is resolved at runtime.
 *
 * @module
 */
import { Context, Effect, Layer } from "effect"
import { RpcClient, RpcSerialization } from "effect/unstable/rpc"
import type { RpcClientError } from "effect/unstable/rpc/RpcClientError"
import type * as RpcGroup from "effect/unstable/rpc/RpcGroup"
import {
  FetchHttpClient,
  HttpClient,
  HttpClientRequest,
} from "effect/unstable/http"
import { HandoffRpc } from "@repo/contracts/rpc/procedures/handoff"
import { SessionRpc } from "@repo/contracts/rpc/procedures/session"

// The worker serves RPC with `RpcSerialization.layerJson`, which sends a JSON
// array of response messages; Effect's default client decode re-wraps it. This
// returns the parsed JSON directly (matching the web app's client transport).
const layerJsonFixed = Layer.succeed(RpcSerialization.RpcSerialization)({
  contentType: "application/json",
  includesFraming: false,
  makeUnsafe: () => {
    const decoder = new TextDecoder()
    return {
      decode: (bytes: string | Uint8Array) => {
        const parsed = JSON.parse(
          typeof bytes === "string" ? bytes : decoder.decode(bytes),
        )
        return Array.isArray(parsed) ? parsed : [parsed]
      },
      encode: (response: unknown) => JSON.stringify(response),
    }
  },
})

/** Fetch-backed HTTP client that sets a fixed set of headers on every request. */
const headerClient = (
  headers: Record<string, string>,
): Layer.Layer<HttpClient.HttpClient> =>
  Layer.effect(HttpClient.HttpClient)(
    Effect.gen(function* () {
      const base = yield* HttpClient.HttpClient
      return base.pipe(
        HttpClient.mapRequest((request) => {
          let req = request
          for (const [key, value] of Object.entries(headers)) {
            req = HttpClientRequest.setHeader(req, key, value)
          }
          return req
        }),
      )
    }),
  ).pipe(Layer.provide(FetchHttpClient.layer))

/** A complete RPC protocol layer (HTTP + JSON) pointed at `<baseUrl>/rpc`. */
const protocol = (baseUrl: string, headers: Record<string, string>) =>
  RpcClient.layerProtocolHttp({ url: `${baseUrl}/rpc` }).pipe(
    Layer.provide(layerJsonFixed),
    Layer.provide(headerClient(headers)),
    // Fresh transport per client — prevents Effect from memoizing/sharing the
    // HttpClient with any other in scope (which collides requestId counters).
    Layer.fresh,
  )

// ── Clients ────────────────────────────────────────────────────────────────

export class HandoffClient extends Context.Service<
  HandoffClient,
  RpcClient.RpcClient<RpcGroup.Rpcs<typeof HandoffRpc>, RpcClientError>
>()("heysegment-cli/HandoffClient") {
  static readonly Live = Layer.effect(HandoffClient)(RpcClient.make(HandoffRpc))
}

export class SessionClient extends Context.Service<
  SessionClient,
  RpcClient.RpcClient<RpcGroup.Rpcs<typeof SessionRpc>, RpcClientError>
>()("heysegment-cli/SessionClient") {
  static readonly Live = Layer.effect(SessionClient)(RpcClient.make(SessionRpc))
}

/** HandoffClient over an unauthenticated transport to `baseUrl`. */
export const handoffLayer = (baseUrl: string): Layer.Layer<HandoffClient> =>
  HandoffClient.Live.pipe(Layer.provide(protocol(baseUrl, {})))

/** SessionClient over a session-token transport to `baseUrl`. */
export const sessionLayer = (
  baseUrl: string,
  auth: { readonly sessionId: string; readonly token: string },
): Layer.Layer<SessionClient> =>
  SessionClient.Live.pipe(
    Layer.provide(
      protocol(baseUrl, {
        authorization: `Bearer ${auth.token}`,
        "x-session-id": auth.sessionId,
      }),
    ),
  )
